import React, { useCallback, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { intl } from 'lang/intl'

import { TtIconButton } from '@23/ui-react'

import {
  removeOverlay,
  setChyron,
  useOverlayState,
} from '../../../../../firebase/hooks/useOverlays'
import { Toggle } from '../../../../components'
import type { JoinToSpeakRequest } from '../../../../join-to-speak'
import { useJoinToSpeakAsHost } from '../../../../join-to-speak'
import { WebinarPanelComponent } from '../index'
import { ChyronType } from '../../../../../firebase/model/chyron-model'
import {
  useLiveDataQuery,
  useUpdateOnCameraState,
} from '../../../../../global/blocs/useLiveData'

import { JoinToSpeakItem } from './join-to-speak-item'

import './join-to-speak-list.scss'

type JoinToSpeakListViewProps = {
  requests: JoinToSpeakRequest[]
  onAttendeePromotion: (attendeeId: number) => Promise<void>
  onAttendeeRejection: (attendeeId: number) => Promise<void>
}

const JoinToSpeakListView = ({
  requests,
  onAttendeePromotion,
  onAttendeeRejection,
}: JoinToSpeakListViewProps) => {
  const overlay = useOverlayState()
  const chyronIsShowing = overlay?.type === ChyronType.J2SIntroduction
  const { data: liveData } = useLiveDataQuery()
  const updateOnCameraState = useUpdateOnCameraState()

  // Auto-remove chyron when feature is disabled
  useEffect(() => {
    if (liveData?.allow_guest_speakers_p === false && chyronIsShowing) {
      void removeOverlay()
    }
  }, [liveData?.allow_guest_speakers_p, chyronIsShowing])

  const handleToggleGoOnCamera = useCallback(
    (toggled: boolean) => {
      updateOnCameraState(toggled)
    },
    [updateOnCameraState]
  )

  return (
    <div className="section">
      <div className="section-title">
        <FormattedMessage defaultMessage="Asking to go on camera" />
        {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
        {requests.length > 0 && ` (${requests.length})`}
        {liveData?.allow_guest_speakers_p === true && (
          <TtIconButton
            name="eye"
            label={intl.formatMessage({
              defaultMessage: 'Toggle Go on camera TvCard',
            })}
            library="23"
            className="show-j2s-intro-chyron-button"
            onClick={() =>
              setChyron(
                chyronIsShowing ? null : { type: ChyronType.J2SIntroduction }
              )
            }
            data-active={chyronIsShowing}
          />
        )}
      </div>
      <WebinarPanelComponent>
        <div className="go-on-camera-toggle-wrapper">
          <FormattedMessage defaultMessage="Audience can ask to go on camera" />
          <Toggle
            onChange={handleToggleGoOnCamera}
            toggled={liveData?.allow_guest_speakers_p === true}
            ariaLabel="Enable go on camera"
          />
        </div>

        {liveData?.allow_guest_speakers_p !== false &&
          (requests.length > 0 ? (
            <div className="requests">
              {requests.map((joinRequest) => (
                <JoinToSpeakItem
                  key={joinRequest.attendeeId}
                  attendeeId={joinRequest.attendeeId}
                  attendeeName={joinRequest.attendeeName}
                  attendeeFullName={joinRequest.attendeeFullName}
                  attendeeEmail={joinRequest.attendeeEmail}
                  question={joinRequest.question}
                  handleAttendeePromotion={onAttendeePromotion}
                  handleAttendeeRejection={onAttendeeRejection}
                  previewImageUrl={joinRequest.previewImageUrl}
                />
              ))}
            </div>
          ) : (
            <div className="empty-j2s-requests">
              <FormattedMessage defaultMessage="You will see requests here" />
            </div>
          ))}
      </WebinarPanelComponent>
    </div>
  )
}

const JoinToSpeakList = () => {
  const { requests, handleAttendeePromotion, handleAttendeeDenial } =
    useJoinToSpeakAsHost()

  return (
    <div className="join-request-list">
      <JoinToSpeakListView
        requests={requests}
        onAttendeePromotion={handleAttendeePromotion}
        onAttendeeRejection={handleAttendeeDenial}
      />
    </div>
  )
}

export { JoinToSpeakList }
